import { useOutletContext } from "react-router-dom";
import { Link } from "react-router-dom";
import { CalendarCheck, ShoppingCart, Inbox, ShieldCheck, Briefcase, UserSquare2, ArrowRight, PlayCircle, CalendarClock, AlertTriangle } from "lucide-react";
import { useAuth } from "../../lib/auth";

const CARDS = [
  { key: "bookings", label: "Organizational Bookings", icon: CalendarCheck, to: "/care-portal/admin/bookings", roles: ["owner", "admin"] },
  { key: "bulk_orders", label: "Bulk Orders", icon: ShoppingCart, to: "/care-portal/admin/bulk-orders", roles: ["owner", "admin"] },
  { key: "inbox", label: "Inbox", icon: Inbox, to: "/care-portal/admin/inbox", roles: ["owner", "admin"] },
  { key: "care_portal", label: "Care Portal", icon: ShieldCheck, to: "/care-portal/admin/support-requests", roles: ["owner", "admin", "chaplain"] },
];

const CAREER_CARDS = [
  { key: "new_applications", label: "New Applications", icon: UserSquare2, to: "/care-portal/admin/applications" },
  { key: "active_jobs", label: "Active Job Postings", icon: Briefcase, to: "/care-portal/admin/jobs" },
];

export default function AdminOverview() {
  const { overview } = useOutletContext();
  const { user } = useAuth();
  const role = user?.role || "admin";
  const isChaplain = role === "chaplain";
  const visibleCards = CARDS.filter((c) => c.roles.includes(role));
  const careers = overview?.careers;
  const followUps = overview?.follow_ups_due_this_week;
  const followUpCount = followUps?.count ?? null;
  const followUpTo = followUpCount != null && followUps?.date_from && followUps?.date_to
    ? `/care-portal/admin/activity-reports?follow_up_needed=true&follow_up_date_from=${followUps.date_from}&follow_up_date_to=${followUps.date_to}`
    : "/care-portal/admin/activity-reports?follow_up_needed=true";
  const overdue = overview?.overdue_follow_ups;
  const overdueCount = overdue?.count ?? null;
  const overdueTo = overdueCount != null && overdue?.follow_up_date_to
    ? `/care-portal/admin/activity-reports?follow_up_needed=true&follow_up_date_to=${overdue.follow_up_date_to}`
    : "/care-portal/admin/activity-reports?follow_up_needed=true";
  const followUpWarn = (followUpCount ?? 0) > 0;
  const overdueWarn = (overdueCount ?? 0) > 0;
  return (
    <div data-testid="admin-overview">
      <h2 className="font-heading text-2xl tracking-tight text-slate-900">Overview</h2>
      <p className="mt-1 text-sm text-slate-500">Live snapshot of what's waiting for you.</p>
      <div className="mt-6 grid sm:grid-cols-2 gap-5">
        {visibleCards.map((c) => {
          const stats = overview?.[c.key];
          return (
            <Link key={c.key} to={c.to} data-testid={`overview-card-${c.key}`} className="group bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#4A6FA5] hover:shadow-md transition-all">
              <div className="flex items-start justify-between">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-[#F0F4F8] text-[#4A6FA5]"><c.icon className="w-5 h-5" strokeWidth={1.75} /></span>
                <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#4A6FA5]" />
              </div>
              <p className="mt-5 text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold">New</p>
              <p className="font-heading text-4xl text-slate-900 mt-1" data-testid={`overview-new-${c.key}`}>{stats?.new ?? "—"}</p>
              <p className="mt-1 text-sm text-slate-600">{c.label} · <span className="text-slate-500">{stats?.total ?? 0} total</span></p>
            </Link>
          );
        })}
        <Link
          to={followUpTo}
          data-testid="overview-card-follow-ups-due-this-week"
          data-warn={followUpWarn ? "true" : "false"}
          className={`group rounded-2xl p-6 border transition-all hover:shadow-md ${followUpWarn ? "bg-amber-50/70 border-amber-300 hover:border-amber-400" : "bg-white border-slate-200 hover:border-[#4A6FA5]"}`}
        >
          <div className="flex items-start justify-between">
            <span className={`inline-flex items-center justify-center w-10 h-10 rounded-lg ${followUpWarn ? "bg-amber-100 text-amber-700" : "bg-[#F0F4F8] text-[#4A6FA5]"}`}><CalendarClock className="w-5 h-5" strokeWidth={1.75} /></span>
            <ArrowRight className={`w-4 h-4 ${followUpWarn ? "text-amber-500 group-hover:text-amber-700" : "text-slate-400 group-hover:text-[#4A6FA5]"}`} />
          </div>
          <p className={`mt-5 text-xs uppercase tracking-[0.16em] font-semibold ${followUpWarn ? "text-amber-800" : "text-slate-500"}`}>This Week</p>
          <p className={`font-heading text-4xl mt-1 ${followUpWarn ? "text-amber-900" : "text-slate-900"}`} data-testid="overview-follow-ups-due-count">{followUpCount ?? "—"}</p>
          <p className={`mt-1 text-sm ${followUpWarn ? "text-amber-800" : "text-slate-600"}`}>Follow-Ups Due This Week · <span className={followUpWarn ? "text-amber-700" : "text-slate-500"}>{followUps?.date_from && followUps?.date_to ? `${followUps.date_from} → ${followUps.date_to}` : "Mon–Sun"}</span></p>
        </Link>
        <Link
          to={overdueTo}
          data-testid="overview-card-overdue-follow-ups"
          data-warn={overdueWarn ? "true" : "false"}
          className={`group rounded-2xl p-6 border transition-all hover:shadow-md ${overdueWarn ? "bg-amber-50/70 border-amber-300 hover:border-amber-400" : "bg-white border-slate-200 hover:border-[#4A6FA5]"}`}
        >
          <div className="flex items-start justify-between">
            <span className={`inline-flex items-center justify-center w-10 h-10 rounded-lg ${overdueWarn ? "bg-amber-100 text-amber-700" : "bg-[#F0F4F8] text-[#4A6FA5]"}`}><AlertTriangle className="w-5 h-5" strokeWidth={1.75} /></span>
            <ArrowRight className={`w-4 h-4 ${overdueWarn ? "text-amber-500 group-hover:text-amber-700" : "text-slate-400 group-hover:text-[#4A6FA5]"}`} />
          </div>
          <p className={`mt-5 text-xs uppercase tracking-[0.16em] font-semibold ${overdueWarn ? "text-amber-800" : "text-slate-500"}`}>Overdue</p>
          <p className={`font-heading text-4xl mt-1 ${overdueWarn ? "text-amber-900" : "text-slate-900"}`} data-testid="overview-overdue-follow-ups-count">{overdueCount ?? "—"}</p>
          <p className={`mt-1 text-sm ${overdueWarn ? "text-amber-800" : "text-slate-600"}`}>Overdue Follow-Ups · <span className={overdueWarn ? "text-amber-700" : "text-slate-500"}>before {overdue?.before || "today"}</span></p>
        </Link>
      </div>

      {!isChaplain && (
        <>
          <h3 className="mt-10 font-heading text-xl tracking-tight text-slate-900">Careers</h3>
          <p className="mt-1 text-sm text-slate-500">Employment opportunities and applications.</p>
          <div className="mt-4 grid sm:grid-cols-2 gap-5">
            {CAREER_CARDS.map((c) => {
              const value = c.key === "new_applications" ? careers?.applications?.new : careers?.jobs?.active;
              const total = c.key === "new_applications" ? careers?.applications?.total : careers?.jobs?.draft;
              const totalLabel = c.key === "new_applications" ? "total" : "draft";
              return (
                <Link key={c.key} to={c.to} data-testid={`overview-card-careers-${c.key}`} className="group bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#4A6FA5] hover:shadow-md transition-all">
                  <div className="flex items-start justify-between">
                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-[#F0F4F8] text-[#4A6FA5]"><c.icon className="w-5 h-5" strokeWidth={1.75} /></span>
                    <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#4A6FA5]" />
                  </div>
                  <p className="mt-5 text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold">{c.key === "new_applications" ? "New" : "Active"}</p>
                  <p className="font-heading text-4xl text-slate-900 mt-1" data-testid={`overview-careers-${c.key}`}>{value ?? "—"}</p>
                  <p className="mt-1 text-sm text-slate-600">{c.label} · <span className="text-slate-500">{total ?? 0} {totalLabel}</span></p>
                </Link>
              );
            })}
          </div>

          <h3 className="mt-10 font-heading text-xl tracking-tight text-slate-900">Demonstration Environments</h3>
          <p className="mt-1 text-sm text-slate-500">Isolated sample environments for prospective-company presentations. No production data.</p>
          <div className="mt-4 grid sm:grid-cols-2 gap-5">
            <Link
              to="/care-portal/demo"
              data-testid="overview-card-demo-environments"
              className="group bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#4A6FA5] hover:shadow-md transition-all"
            >
              <div className="flex items-start justify-between">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-amber-50 text-amber-700">
                  <PlayCircle className="w-5 h-5" strokeWidth={1.75} />
                </span>
                <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#4A6FA5]" />
              </div>
              <p className="mt-5 text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold">Demo Hub</p>
              <p className="font-heading text-xl text-slate-900 mt-1">CALLACHAP · BALM Demonstration</p>
              <p className="mt-1 text-sm text-slate-600">Employee experience + Employer utilization dashboard · <span className="text-slate-500">isolated from live data</span></p>
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
